import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type Report = {
  id: string;
  year: number;
  quarter: number;
  ideas_engaged: number;
  scopes_proposed: number;
  projects_shipped: number;
  dollars_deployed: number;
  lessons: string | null;
  highlights: string | null;
  submitted_at: string;
};

function currentQuarter(): { year: number; quarter: number } {
  const d = new Date();
  return { year: d.getFullYear(), quarter: Math.floor(d.getMonth() / 3) + 1 };
}

export default function OutcomesTab({ orgId }: { orgId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const initial = currentQuarter();
  const [form, setForm] = useState({
    year: initial.year,
    quarter: initial.quarter,
    ideas_engaged: 0,
    scopes_proposed: 0,
    projects_shipped: 0,
    dollars_deployed: 0,
    lessons: "",
    highlights: "",
  });

  const reportsQ = useQuery({
    queryKey: ["outcomes-reports", orgId],
    queryFn: async (): Promise<Report[]> => {
      const { data, error } = await supabase
        .from("partner_outcomes_reports")
        .select("id, year, quarter, ideas_engaged, scopes_proposed, projects_shipped, dollars_deployed, lessons, highlights, submitted_at")
        .eq("affiliate_id", orgId)
        .order("year", { ascending: false })
        .order("quarter", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Report[];
    },
  });

  const totals = useMemo(() => {
    const rows = reportsQ.data ?? [];
    return rows.reduce(
      (acc, r) => ({
        ideas: acc.ideas + r.ideas_engaged,
        scopes: acc.scopes + r.scopes_proposed,
        projects: acc.projects + r.projects_shipped,
        dollars: acc.dollars + Number(r.dollars_deployed ?? 0),
      }),
      { ideas: 0, scopes: 0, projects: 0, dollars: 0 },
    );
  }, [reportsQ.data]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Sign in required");
      const { error } = await supabase.from("partner_outcomes_reports").upsert(
        {
          affiliate_id: orgId,
          year: form.year,
          quarter: form.quarter,
          ideas_engaged: form.ideas_engaged,
          scopes_proposed: form.scopes_proposed,
          projects_shipped: form.projects_shipped,
          dollars_deployed: form.dollars_deployed,
          lessons: form.lessons.trim() || null,
          highlights: form.highlights.trim() || null,
          submitted_by: user.id,
          submitted_at: new Date().toISOString(),
        },
        { onConflict: "affiliate_id,year,quarter" },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Quarterly report saved");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["outcomes-reports", orgId] });
    },
    onError: (e: any) => toast.error(e.message ?? "Could not save report"),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("partner_outcomes_reports").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Report removed");
      qc.invalidateQueries({ queryKey: ["outcomes-reports", orgId] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Ideas engaged" value={totals.ideas.toLocaleString()} />
        <MetricCard label="Scopes proposed" value={totals.scopes.toLocaleString()} />
        <MetricCard label="Projects shipped" value={totals.projects.toLocaleString()} />
        <MetricCard label="Dollars deployed" value={`$${totals.dollars.toLocaleString()}`} />
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg">Quarterly reports</h3>
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> New report
        </Button>
      </div>

      {reportsQ.isLoading ? (
        <div className="py-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (reportsQ.data ?? []).length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No quarterly reports submitted yet. Reports roll up into anonymized ecosystem dashboards.
        </Card>
      ) : (
        <div className="grid gap-3">
          {(reportsQ.data ?? []).map((r) => (
            <Card key={r.id} className="p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-display text-base">
                    Q{r.quarter} {r.year}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Submitted {new Date(r.submitted_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Badge variant="secondary">{r.ideas_engaged} ideas</Badge>
                  <Badge variant="secondary">{r.scopes_proposed} scopes</Badge>
                  <Badge variant="secondary">{r.projects_shipped} shipped</Badge>
                  <Badge variant="secondary">${Number(r.dollars_deployed).toLocaleString()}</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (confirm("Delete this quarterly report?")) del.mutate(r.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {(r.highlights || r.lessons) && (
                <div className="mt-3 space-y-2 text-sm">
                  {r.highlights && (
                    <p>
                      <span className="font-medium">Highlights:</span> {r.highlights}
                    </p>
                  )}
                  {r.lessons && (
                    <p className="text-muted-foreground">
                      <span className="font-medium text-foreground">Lessons:</span> {r.lessons}
                    </p>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Quarterly outcomes report</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Year</Label>
              <Input
                type="number"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label>Quarter</Label>
              <Select
                value={String(form.quarter)}
                onValueChange={(v) => setForm({ ...form, quarter: Number(v) })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map((q) => (
                    <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <NumField label="Ideas engaged" value={form.ideas_engaged} onChange={(v) => setForm({ ...form, ideas_engaged: v })} />
            <NumField label="Scopes proposed" value={form.scopes_proposed} onChange={(v) => setForm({ ...form, scopes_proposed: v })} />
            <NumField label="Projects shipped" value={form.projects_shipped} onChange={(v) => setForm({ ...form, projects_shipped: v })} />
            <NumField label="Dollars deployed ($)" value={form.dollars_deployed} onChange={(v) => setForm({ ...form, dollars_deployed: v })} />
            <div className="md:col-span-2">
              <Label>Highlights</Label>
              <Textarea rows={2} value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} placeholder="Named wins from this quarter" />
            </div>
            <div className="md:col-span-2">
              <Label>Lessons learned</Label>
              <Textarea rows={3} value={form.lessons} onChange={(e) => setForm({ ...form, lessons: e.target.value })} placeholder="What worked, what didn't, what you'd do differently" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
              {submit.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </Card>
  );
}

function NumField({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </div>
  );
}